import React from 'react';
import { NavLink } from 'react-router-dom';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { recurringExpensesApi, type RecurringExpense } from '../../api/recurringExpenses';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

function countDueSoon(expenses: RecurringExpense[]): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in7 = new Date(today);
  in7.setDate(today.getDate() + 7);
  return expenses.filter((e) => {
    if (!e.is_active) return false;
    const d = new Date(e.next_due_date);
    d.setHours(0, 0, 0, 0);
    return d <= in7;
  }).length;
}

type NavItem = {
  path: string;
  label: string;
  emoji: string;
  badgeKey?: string;
};

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', emoji: '\uD83D\uDCCA' },
  { path: '/receipts', label: 'Belege', emoji: '\uD83E\uDDFE' },
  { path: '/shopping-list', label: 'Einkaufsliste', emoji: '\uD83D\uDED2' },
  { path: '/todos', label: 'Aufgaben', emoji: '\u2611\uFE0F' },
  { path: '/receipts/new', label: 'Neuer Beleg', emoji: '\u2795' },
  { path: '/stats', label: 'Statistiken', emoji: '\uD83D\uDCC8' },
  { path: '/recurring', label: 'Fixkosten', emoji: '\uD83D\uDD04', badgeKey: 'recurring' },
  { path: '/prices', label: 'Preisvergleich', emoji: '\uD83D\uDCB0' },
  { path: '/settings', label: 'Einstellungen', emoji: '\u2699\uFE0F' },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const user = useAuthStore((s) => s.user);
  const { mode, isDark, toggle: toggleTheme } = useThemeStore();
  const firstLetter = (user?.name || user?.display_name || 'U').charAt(0).toUpperCase();

  const { data: recurringExpenses = [] } = useQuery<RecurringExpense[]>({
    queryKey: ['recurring-expenses'],
    queryFn: recurringExpensesApi.list,
    staleTime: 5 * 60 * 1000,
  });
  const dueSoonCount = countDueSoon(recurringExpenses);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64
          bg-white/70 backdrop-blur-2xl border-r border-white/40
          dark:bg-[#1a1a2e]/80 dark:border-indigo-500/10
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 pt-6 pb-4 lg:pt-6">
          <img src="/logo.png" alt="PaarKurs" className="w-10 h-10 rounded-2xl object-contain shadow-glow-primary" />
          <div>
            <span className="text-lg font-bold text-neutral-800 dark:text-neutral-100 tracking-tight">PaarKurs</span>
            <span className="block text-[10px] text-neutral-400 dark:text-neutral-500 font-medium -mt-0.5">Gemeinsam sparen</span>
          </div>
          {/* Mobile close */}
          <button
            onClick={onClose}
            className="ml-auto p-2 rounded-xl text-neutral-400 hover:bg-neutral-100/80 dark:hover:bg-white/10 transition-colors lg:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-3 mt-4 space-y-1">
          {navItems.map((item, index) => {
            const badge = item.badgeKey === 'recurring' && dueSoonCount > 0 ? dueSoonCount : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                   transition-all duration-200 group
                   animate-slide-up animate-fill-both
                  ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-500/10 via-primary-500/5 to-transparent text-primary-600 dark:text-primary-400 shadow-sm border border-primary-100/50 dark:border-primary-500/20'
                      : 'text-neutral-500 dark:text-neutral-400 hover:bg-white/60 dark:hover:bg-white/5 hover:text-neutral-700 dark:hover:text-neutral-200 hover:translate-x-1'
                  }`
                }
                style={{ animationDelay: `${index * 60}ms` }}
              >
                <span className="text-lg group-hover:scale-110 transition-transform duration-200">{item.emoji}</span>
                <span className="flex-1">{item.label}</span>
                {badge > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full
                                   bg-amber-500 text-white text-[10px] font-bold shadow-sm shrink-0"
                        aria-label={`${badge} bald faellig`}
                  >
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Theme Toggle + User Info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3">
          {/* Dark/Light Toggle */}
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium
                       transition-all duration-300 group
                       text-neutral-500 dark:text-neutral-400
                       hover:bg-white/60 dark:hover:bg-white/5
                       hover:text-neutral-700 dark:hover:text-neutral-200"
          >
            <span className="text-lg transition-transform duration-300 group-hover:scale-110">
              {mode === 'system' ? '\uD83D\uDCBB' : isDark ? '\u2600\uFE0F' : '\uD83C\uDF19'}
            </span>
            <span>{mode === 'system' ? 'System (Auto)' : isDark ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/50 dark:bg-white/5 border border-white/40 dark:border-indigo-500/10">
            <div className="w-9 h-9 gradient-primary rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
              {firstLetter}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                {user?.name || user?.display_name || 'Benutzer'}
              </p>
              <p className="text-[11px] text-neutral-400 dark:text-neutral-500 truncate">
                {user?.email || ''}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
